import { PAGE_LINKS } from '@/router/pageConst'
import styles from './ProfessorProjectDetailPage.module.less'
import AIcon from '@/components/A/AIcon/AIcon'
import MiddleText from '@/components/MiddleText/MiddleText'
import InfoDrawerBtn from '../ProjectDetailPage/components/InfoDrawerBtn/InfoDrawerBtn'
import CategoryTab, {
  CategoryTabKey,
} from '../ProjectDetailPage/components/CategoryTab/CategoryTab'
import VideoAnalysis from '../ProjectDetailPage/components/VideoAnalysis/VideoAnalysis'
import { ProfessorContext, ProjectDetailPageContext } from '@/Contexts/Contexts'
import StoryboardAnalysis from '../ProjectDetailPage/components/StoryboardAnalysis/StoryboardAnalysis'
import HeatMapAnalysis from '../ProjectDetailPage/components/HeatMapAnalysis/HeatMapAnalysis'
import { commonUtils } from '@/helpers/commonUtils'
import AButton from '@/components/A/AButton/AButton'
import AnalyzingView from '../ProjectDetailPage/components/AnalyzingView/AnalyzingView'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { LanguageEnum } from '@/commonConst'
import { useAutoRequest, useRequest } from 'alova/client'
import { api } from '@/api'
import { message, Spin } from 'antd'
import { usePollingProgress } from '../ProjectPage/components/ProjectPageContent/ProjectPageContent'
import { useAnimationProjects } from '../ProjectDetailPage/ProjectDetailPage'
import cls from 'classnames'
import ProfessorRatePanel from './components/ProfessorRatePanel/ProfessorRatePanel'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import NiceModal from '@ebay/nice-modal-react'
import CommonModal, { CommonModalProps } from '@/components/Modals/CommonModal/CommonModal'

function useTranslationChange() {
  const location = useLocation()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const [startPollingTranslateStatus, setStartPollingTranslateStatus] = useState(false)
  const {
    data,
    loading,
    send: request,
  } = useRequest(api.project.projectDetail, {
    immediate: false,
  })
  const sendRequest = () => {
    request(location.state.id, LanguageEnum[i18n.language as keyof typeof LanguageEnum])
  }
  const { data: translateStatusData } = useAutoRequest(
    () =>
      api.project.projectTranslateStatus(
        location.state.id,
        LanguageEnum[i18n.language as keyof typeof LanguageEnum]
      ),
    {
      pollingTime: 1000,
      middleware: (_, next) => {
        if (startPollingTranslateStatus) {
          next()
        }
      },
    }
  )

  useEffect(() => {
    if (location.state && location.state.id) {
      sendRequest()
    } else {
      navigate(PAGE_LINKS.project)
      message.warning(t('dashboard.projectDetailPage.message.projectNotFound'))
    }
  }, [i18n.language])

  useEffect(() => {
    if (data && data.data.progressRatio === 100 && !data.hasTranslated) {
      setStartPollingTranslateStatus(true)
    }
  }, [data])

  useEffect(() => {
    if (translateStatusData && translateStatusData.translateStatus === 'completed') {
      setStartPollingTranslateStatus(false)
      sendRequest()
    }
  }, [translateStatusData])

  return { data, loading, send: sendRequest }
}

function ProfessorProjectDetailPage() {
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { data, send, loading } = useTranslationChange()
  const [project, setProject] = useState<ProjectModels.ProjectDetail>()
  const [rateKey, setRateKey] = useState<Models.ProfessorAnalyseModule>()
  const [tabKey, setTabKey] = useState<CategoryTabKey>('primary')
  const { setStartPollingProgress, progressData } = usePollingProgress(
    project && (project.status === 'analyzing' || project.status === 'creating') ? [project.id] : []
  )
  const canDoAnimation = useAnimationProjects(data?.hasTranslated, project)
  const [animationStatus, setAnimationStatus] = useState<{
    videoNeedAnimation: boolean
    storyNeedAnimation: boolean
  }>({
    videoNeedAnimation: false,
    storyNeedAnimation: false,
  })

  const {
    data: { data: expertRateData, expertAnalyseStatus },
  } = useRequest(api.expert.getExpertRate(location.state.id), {
    initialData: {},
  })

  const [activeHeatMapIndex, setActiveHeatMapIndex] = useState<number>(0)

  const [initRender, setInitRender] = useState(false)

  const [professorRateInfoArray, setProfessorRateInfoArray] = useTypedLocalStorage(
    'professor-rate-info',
    {
      defaultValue: [],
    }
  )
  const [professorRateInfo, setProfessorRateInfo] = useState<Models.RateMap>({})
  const [alreadyRatedMap, setAlreadyRatedMap] = useState<Record<keyof Models.RateMap, boolean>>({
    adEffIndex: false,
    subjectiveResponseCurve: false,
    indexAnalyses: false,
    keyResults: false,
    theGolden3s: false,
    // 专家评价调整，这里不展示下面 3 块，节省专家评测时间
    /*     keyMessageMoment: false,
    brandMoment: false,
    interestMoment: false, */
    theEnding3s: false,
    heatMap: false,
  })

  const [unfinishedRateBtn, setUnfinishedRateBtn] = useState<Array<keyof Models.RateMap>>([])

  const handleRetry = () => {
    api.project
      .projectRetry(project!.id)
      .send()
      .then(() => {
        send()
        setStartPollingProgress(true)
      })
  }

  const handleClickHeatMap = (index: number) => {
    setTabKey('heatmap')
    setActiveHeatMapIndex(index)
  }

  const showProfessorPanel = (key: Models.ProfessorAnalyseModule) => {
    setRateKey(key)
  }

  const changeAlreadyRatedMap = (k: keyof Models.RateMap) => {
    setAlreadyRatedMap((prev) => ({
      ...prev,
      [k]: true,
    }))
  }

  const showRateLocatedMessage = () => {
    message.warning(t('professor.message.rateLocation'))
  }

  const handleReturn = () => {
    if (unfinishedRateBtn.length > 0) {
      const props: CommonModalProps = {
        title: t('professor.unfinishedTitle'),
        content: t('professor.unfinishedRate'),
        confirmText: t('professor.unfinishedConfirm'),
        cancelText: t('professor.unfinishedCancel'),
        closable: false,
        onConfirm: () => {
          setUnfinishedRateBtn((rateBtn) => {
            return [...rateBtn]
          })
          showRateLocatedMessage()
        },
        onCancel: () => {
          navigate(PAGE_LINKS.professorProject)
        },
      }

      NiceModal.show(CommonModal, props)
    } else {
      navigate(PAGE_LINKS.professorProject)
    }
  }

  useEffect(() => {
    if (data) {
      setProject(data.data)
    }
  }, [data])

  useEffect(() => {
    if (expertAnalyseStatus === 'part' || expertAnalyseStatus === 'untreated') {
      showRateLocatedMessage()
    }
  }, [expertAnalyseStatus])

  useEffect(() => {
    if (project && (project.status === 'analyzing' || project.status === 'creating')) {
      setStartPollingProgress(true)
    }
  }, [project])

  useEffect(() => {
    if (progressData && project && progressData[project.id]) {
      const status =
        progressData[project.id].process === 100 ? progressData[project.id].status : 'analyzing'
      if (status === 'completed' || status === 'failed') {
        send()
      }
    }
  }, [progressData])

  useEffect(() => {
    if (data) {
      const project = data.data
      if (
        progressData[project.id]?.process === undefined ||
        project.status === 'completed' ||
        project.status === 'failed' ||
        (progressData[project.id]?.process === 100 && data.hasTranslated)
      ) {
        setInitRender(true)
      } else {
        setInitRender(false)
      }
    }
  }, [progressData, data])

  useEffect(() => {
    setAnimationStatus({
      storyNeedAnimation: canDoAnimation,
      videoNeedAnimation: canDoAnimation,
    })
  }, [canDoAnimation, i18n.language])

  useEffect(() => {
    setAlreadyRatedMap({
      adEffIndex: Boolean(expertRateData?.adEffIndex),
      subjectiveResponseCurve: Boolean(expertRateData?.subjectiveResponseCurve),
      indexAnalyses: Boolean(expertRateData?.indexAnalyses),
      keyResults: Boolean(expertRateData?.keyResults),
      theGolden3s: Boolean(expertRateData?.theGolden3s),
      // 专家评价调整，这里不展示下面 3 块，节省专家评测时间
      /*       keyMessageMoment: Boolean(expertRateData?.keyMessageMoment),
      brandMoment: Boolean(expertRateData?.brandMoment),
      interestMoment: Boolean(expertRateData?.interestMoment), */
      theEnding3s: Boolean(expertRateData?.theEnding3s),
      heatMap: Boolean(expertRateData?.heatMap),
    })
  }, [expertRateData])

  useEffect(() => {
    const unfinishedRateBtn = Object.keys(alreadyRatedMap).filter(
      (k) => !alreadyRatedMap[k as keyof Models.RateMap]
    ) as Array<keyof Models.RateMap>
    setUnfinishedRateBtn(unfinishedRateBtn)
    const mp: { [k in keyof Models.RateMap]: Models.ProfessorAnalyseModule } = {
      adEffIndex: 'ad_eff_index',
      subjectiveResponseCurve: 'subjective_response_curve',
      indexAnalyses: 'index_analyses',
      keyResults: 'key_results',
      theGolden3s: 'the_golden_3s',
      theEnding3s: 'the_ending_3s',
      heatMap: 'heat_map',
    }
    if (unfinishedRateBtn.length > 0) {
      setRateKey(mp[unfinishedRateBtn[0]])
    }
  }, [alreadyRatedMap])

  useEffect(() => {
    if (unfinishedRateBtn.length === 0) {
      return
    }
    const unfinishedRateBelongs: Array<{
      key: keyof Models.RateMap
      belong: 'primary' | 'detail' | 'heatmap'
    }> = [
        { key: 'adEffIndex', belong: 'primary' },
        { key: 'subjectiveResponseCurve', belong: 'primary' },
        { key: 'indexAnalyses', belong: 'primary' },
        { key: 'keyResults', belong: 'primary' },
        { key: 'theGolden3s', belong: 'detail' },
        // 专家评价调整，这里不展示下面 3 块，节省专家评测时间
        /*       { key: 'keyMessageMoment', belong: 'detail' },
        { key: 'brandMoment', belong: 'detail' },
        { key: 'interestMoment', belong: 'detail' }, */
        { key: 'theEnding3s', belong: 'detail' },
        { key: 'heatMap', belong: 'heatmap' },
      ]
    const unfinishedRateBelong = unfinishedRateBelongs.find(
      (item) => item.key === unfinishedRateBtn[0]
    )
    setTabKey(unfinishedRateBelong?.belong || 'primary')
  }, [unfinishedRateBtn])

  useEffect(() => {
    if (!project || !professorRateInfoArray) {
      return
    }
    const rawRateInfo =
      professorRateInfoArray.find((p) => p.id === project.id) ||
      ({} as { id: number; map: Models.RateMap })
    const professorRateInfo = {
      ...rawRateInfo.map,
      ...expertRateData,
    }
    setProfessorRateInfo(professorRateInfo)
  }, [expertRateData, professorRateInfoArray, project])

  useEffect(() => {
    if (!project) {
      return
    }
    setProfessorRateInfoArray((arr) => {
      const array = arr || []
      const index = array.findIndex((a) => a.id === project.id)
      const allTrue = Object.keys(alreadyRatedMap).every(
        (k) => alreadyRatedMap[k as keyof Models.RateMap]
      )
      if (index === -1 && !allTrue) {
        array.push({ id: project.id, map: professorRateInfo })
      }
      if (index > -1) {
        array.splice(index, 1, { id: project.id, map: professorRateInfo })
      }
      return array
    })
  }, [professorRateInfo, alreadyRatedMap, project])

  useEffect(() => {
    return () => {
      // 离开时，如果所有都提交了则从缓存中删除
      const allTrue = Object.keys(alreadyRatedMap).every((k) => {
        return alreadyRatedMap[k as keyof Models.RateMap]
      })
      if (allTrue) {
        setProfessorRateInfoArray((arr) => {
          const array = arr || []
          return array.filter((a) => a.id !== project?.id)
        })
      }
    }
  }, [project, alreadyRatedMap])

  return (
    <>
      <ProfessorContext.Provider
        value={{
          professorRateInfo: professorRateInfo!,
          changeProfessorRateInfo: setProfessorRateInfo,
          alreadyRatedMap,
          changeAlreadyRatedMap,
          showProfessorPanel,
          unfinishedRateBtn,
        }}
      >
        <div className={styles.professorProjectDetailPage} id='detail' data-testid='professor-project-detail-page'>
          <div className={styles.detailContent}>
            {loading && (
              <div className={styles.loadingBox}>
                <Spin />
              </div>
            )}
            {!loading && project && (
              <>
                <div className={styles.returnBox}>
                  <div className={styles.returnBoxInner}>
                    <div className={styles.returnBtn} onClick={handleReturn} data-testid='project-detail-return-btn'>
                      <span className={styles.iconBox}>
                        <AIcon type='icon-line-global_left' width={14} height={14}></AIcon>
                      </span>
                      <span className={styles.btnText}>
                        <MiddleText text={t('dashboard.projectDetailPage.testResults')} />
                      </span>
                    </div>
                    {(project.status === 'analyzing' || project.status === 'creating') && (
                      <div className={cls(styles.badge, styles.ongoing)}>
                        {t('dashboard.projectDetailPage.ongoing')}
                      </div>
                    )}
                    {project.status === 'failed' && (
                      <div className={cls(styles.badge, styles.failed)}>
                        {t('dashboard.projectDetailPage.failed')}
                      </div>
                    )}
                  </div>
                  <InfoDrawerBtn mode='professor' project={project} />
                </div>
                <div className={styles.detailContentWrapper}>
                  <CategoryTab
                    mode='professor'
                    onChange={setTabKey}
                    activeTab={tabKey}
                    hasTranslated={data.hasTranslated}
                    project={project}
                    onReturn={handleReturn}
                  />
                  <div className={styles.detailContent}>
                    <>
                      {initRender && project.status === 'completed' && (
                        <>
                          {tabKey === 'primary' && (
                            <ProjectDetailPageContext.Provider value={{ handleClickHeatMap }}>
                              <VideoAnalysis
                                mode='professor'
                                progressRatio={progressData}
                                project={project}
                                hasTranslated={data.hasTranslated}
                                needDoAnimation={animationStatus.videoNeedAnimation}
                                onAnimationEnd={() => {
                                  setAnimationStatus((status) => ({
                                    ...status,
                                    videoNeedAnimation: false,
                                  }))
                                }}
                              />
                            </ProjectDetailPageContext.Provider>
                          )}
                          {tabKey === 'detail' && (
                            <StoryboardAnalysis
                              mode='professor'
                              project={project}
                              hasTranslated={data.hasTranslated}
                              needDoAnimation={animationStatus.storyNeedAnimation}
                              onAnimationEnd={() => {
                                setAnimationStatus((status) => ({
                                  ...status,
                                  storyNeedAnimation: false,
                                }))
                              }}
                            />
                          )}
                          {tabKey === 'heatmap' && (
                            <HeatMapAnalysis
                              mode='professor'
                              defaultActiveIndex={activeHeatMapIndex}
                              project={project}
                            />
                          )}
                        </>
                      )}
                      {initRender && project.status === 'failed' && (
                        <div className={styles.failedBox}>
                          <img
                            style={{ width: 140 }}
                            src={commonUtils.getImagePath('projectPage/project-list-empty.png')}
                            alt=''
                          />
                          <p>{t('dashboard.projectDetailPage.videoAnalysisFailed')}</p>
                          <AButton testId='project-detail-retry-btn' type='outlined' shape='round' size='SM' onClick={handleRetry}>
                            {t('dashboard.projectDetailPage.tryAgain')}
                          </AButton>
                        </div>
                      )}
                      {!initRender && (
                        <AnalyzingView
                          ratio={
                            progressData[project.id]?.process > 98
                              ? 98
                              : progressData[project.id]?.process
                          }
                        />
                      )}
                    </>
                  </div>
                </div>
              </>
            )}
          </div>
          <ProfessorRatePanel
            project={project}
            rateKey={rateKey}
            onClose={() => setRateKey(undefined)}
          />
        </div>
      </ProfessorContext.Provider>
    </>
  )
}

export default ProfessorProjectDetailPage
